import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import { ElMessage } from 'element-plus'
import { useTabsStore } from './tabs'
import { hasWorkstationSession, recoverWorkstationSession, cancelWorkstationSession, registerWorkstationStore } from '../services/workstationSession'

export const useUserStore = defineStore('user', () => {
  // 状态
  const user = ref(null)
  const token = ref(localStorage.getItem('token') || '')
  const permissions = ref([])
  const features = ref([])
  const loginTime = ref(localStorage.getItem('loginTime') || null) // ✅ 记录登录时间
  const SESSION_DURATION = 20 * 60 * 60 * 1000 // ✅ 20小时（毫秒）

  // 计算属性
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userRole = computed(() => user.value?.role || '')
  const companyName = computed(() => user.value?.companyName || '')
  const subscriptionPlan = computed(() => user.value?.subscriptionPlan || 'basic')

  // 操作方法
  const setToken = (newToken) => {
    token.value = newToken
    if (newToken) {
      localStorage.setItem('token', newToken)
    } else {
      localStorage.removeItem('token')
    }
  }

  // ✅ 【新增】设置登录时间
  const setLoginTime = (time = Date.now()) => {
    loginTime.value = time
    localStorage.setItem('loginTime', time.toString())
  }

  // ✅ 【新增】检查会话是否超时（超过20小时）
  const isSessionExpired = () => {
    if (!loginTime.value) return false
    const now = Date.now()
    const elapsed = now - parseInt(loginTime.value)
    return elapsed > SESSION_DURATION
  }

  // ✅ 【新增】强制登出（会话超时）
  const forceLogout = async () => {
    if (hasWorkstationSession()) {
      try { await recoverWorkstationSession({ setToken, setUser, setLoginTime }); }
      catch { ElMessage.warning('工作台会话暂不可用，请稍后重试'); }
      return
    }
    ElMessage.warning('您的登录已过期（超过20小时），请重新登录')
    await logout()
    window.location.href = '/login'
  }

  const setUser = (userData) => {
    user.value = userData
    if (userData) {
      features.value = userData.tenant?.features || []
      permissions.value = userData.permissions || []
    }
  }

  const login = async (credentials) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      })

      const data = await response.json()

      if (response.ok) {
        const { token: newToken, user: userData } = data
        setToken(newToken)
        setUser(userData)
        setLoginTime() // ✅ 登录成功时记录当前时间
        ElMessage.success('登录成功')
        return { success: true, user: userData }
      } else {
        ElMessage.error(data.error || '登录失败')
        return { success: false, error: data.error }
      }
    } catch (error) {
      const message = '网络错误，请检查网络连接'
      ElMessage.error(message)
      return { success: false, error: message }
    }
  }

  const logout = async () => {
    cancelWorkstationSession()
    setToken('')
    setUser(null)
    permissions.value = []
    features.value = []
    loginTime.value = null
    localStorage.removeItem('loginTime') // ✅ 清除登录时间

    // ✅ 清除标签页缓存（退出后重新登录时进入工作台，其他标签都关闭）
    const tabsStore = useTabsStore()
    if (tabsStore && typeof tabsStore.clearAllViews === 'function') {
      tabsStore.clearAllViews()
    }

    ElMessage.success('已登出')
  }

  // 权限检查方法
  const hasPermission = (permission) => {
    return permissions.value.includes(permission)
  }

  const hasRole = (role) => {
    if (Array.isArray(role)) {
      return role.includes(userRole.value)
    }
    return userRole.value === role
  }

  const hasFeature = (feature) => {
    return features.value.includes(feature)
  }

  const isAdmin = () => {
    return userRole.value === 'admin'
  }

  const isOperator = () => {
    return ['admin', 'operator'].includes(userRole.value)
  }

  // 验证token有效性并获取完整用户信息
  const validateToken = async () => {
    const savedToken = localStorage.getItem('token')
    if (!savedToken) {
      throw new Error('No token found')
    }

    try {
      // 调用后端验证token接口
      const response = await fetch('/api/auth/validate', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${savedToken}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setUser(data.user)
        return data.user
      } else {
        if (response.status === 401 && hasWorkstationSession()) {
          await recoverWorkstationSession({ setToken, setUser, setLoginTime })
          return user.value
        }
        throw new Error('Token validation failed')
      }
    } catch (error) {
      console.error('Token validation error:', error)
      // 如果验证失败，清除token
      if (!hasWorkstationSession()) {
        localStorage.removeItem('token')
        token.value = ''
      }
      throw error
    }
  }

  // 初始化认证状态
  const initAuth = () => {
    // 从localStorage加载token和登录时间
    const savedToken = localStorage.getItem('token')
    const savedLoginTime = localStorage.getItem('loginTime')

    if (savedLoginTime) {
      loginTime.value = parseInt(savedLoginTime)

      // ✅ 检查会话是否超时（超过20小时）
      if (isSessionExpired()) {
        console.warn('会话已过期（超过20小时），强制登出')
        forceLogout()
        return
      }
    }

    if (savedToken) {
      token.value = savedToken

      // 尝试解析token获取用户信息（简化版本）
      try {
        const tokenParts = savedToken.split('.')
        if (tokenParts.length === 3) {
          // 正确处理Base64解码，支持UTF-8字符（包括中文）
          const base64Url = tokenParts[1]
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
          const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          }).join(''))
          const payload = JSON.parse(jsonPayload)

          // 从token中恢复基本用户信息
          user.value = {
            id: payload.id || payload.userId,
            email: payload.email,
            name: payload.name || '用户',
            role: payload.role,
            tenantId: payload.tenantId,
            isGuest: payload.role === 'guest'
          }
          console.log('已从本地存储恢复登录状态:', user.value)
        }
      } catch (error) {
        console.error('解析token失败:', error)
        // 如果token无效，清除本地存储
        localStorage.removeItem('token')
        token.value = ''
      }
    }
  }

  // 重置状态
  const reset = () => {
    cancelWorkstationSession()
    user.value = null
    token.value = ''
    permissions.value = []
    features.value = []
    localStorage.removeItem('token')
  }

  registerWorkstationStore({ setToken, setUser, setLoginTime, get user() { return user.value } })
  return {
    // 状态
    user: readonly(user),
    token: readonly(token),
    permissions: readonly(permissions),
    features: readonly(features),
    loginTime: readonly(loginTime), // ✅ 导出登录时间

    // 计算属性
    isAuthenticated,
    userRole,
    companyName,
    subscriptionPlan,

    // 方法
    login,
    logout,
    setUser,
    setToken,
    setLoginTime, // ✅ 导出设置登录时间方法
    isSessionExpired, // ✅ 导出检查会话超时方法
    forceLogout, // ✅ 导出强制登出方法
    initAuth,
    validateToken,
    hasPermission,
    hasRole,
    hasFeature,
    isAdmin,
    isOperator,
    reset
  }
})
